import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import * as moment from 'moment';

/** A quick-pick button: `months` whole calendar months, ending with the last complete one. */
export interface DateRangePreset {
  label: string;
  months: number;
}

export interface AppliedDateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

/**
 * The CONTENTS of a date-range menu — start, end, optional quick picks, and Apply.
 *
 * Deliberately not the `<mat-menu>` itself. The consumer owns the menu and the trigger:
 *
 *   <button [matMenuTriggerFor]="rangeMenu">…</button>
 *   <mat-menu #rangeMenu="matMenu">
 *     <app-date-range-menu [startDate]="…" (rangeApplied)="…"></app-date-range-menu>
 *   </mat-menu>
 *
 * That keeps each trigger's own styling, disabled state and label with the button it belongs to,
 * while the panel — which is the part worth sharing — is written once. Two copies of this markup
 * would drift a fix at a time, which is exactly how the statistics page ended up with a pencil
 * that did not match the icons beside it.
 *
 * ─── WHY APPLY EXISTS ───────────────────────────────────────────────────────────────
 *
 * The picker it replaces reloaded the whole statistics page on every `dateChange`. Choosing a
 * range means touching two fields, so the intermediate state — a new start against the OLD end —
 * is always nonsense, and it was always queried: a user narrowing from a year to a month watched
 * the page load a range they had not asked for before it loaded the one they had. Apply makes the
 * half-chosen state unobservable.
 */
@Component({
  selector: 'app-date-range-menu',
  templateUrl: './date-range-menu.component.html',
  styleUrls: ['./date-range-menu.component.scss'],
})
export class DateRangeMenuComponent implements OnChanges {
  /** Seeds the fields when the menu opens. Edits are local until Apply. */
  @Input() startDate: moment.Moment | null = null;
  @Input() endDate: moment.Moment | null = null;

  @Input() maxDate: moment.Moment = moment();
  @Input() presets: DateRangePreset[] = [];
  @Input() applyLabel = 'Apply';
  /** Optional line under the fields — used to say that whole months are reported. */
  @Input() hint: string | null = null;

  @Output() rangeApplied = new EventEmitter<AppliedDateRange>();

  /**
   * The working copies.
   *
   * ⚠ EDITED LOCALLY, NEVER THROUGH THE `@Input`s. Binding the pickers straight to the inputs
   * would mutate the parent's state as the user typed, so dismissing the menu without applying
   * would still have changed the range — which is the behaviour Apply exists to remove.
   */
  start: moment.Moment | null = null;
  end: moment.Moment | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDate']) this.start = this.startDate ? this.startDate.clone() : null;
    if (changes['endDate']) this.end = this.endDate ? this.endDate.clone() : null;
  }

  /**
   * Whole calendar months, ending with the last COMPLETE month.
   *
   * The current month is included only on its last day — the same rule the twelve-month export
   * has always used, kept identical on purpose so the "12 Months" preset reproduces the old
   * button exactly rather than approximately. A partial current month in a monthly breakdown
   * reads as a collapse in activity rather than as a month that has not finished.
   */
  applyPreset(preset: DateRangePreset): void {
    const now = moment();
    const monthEnded = now.date() === now.clone().endOf('month').date();
    const lastMonth = monthEnded ? now.clone() : now.clone().subtract(1, 'month');

    this.end = lastMonth.clone().endOf('month');
    this.start = lastMonth.clone().subtract(preset.months - 1, 'months').startOf('month');
  }

  /** True when this preset's range is exactly what is currently in the fields. */
  isPresetActive(preset: DateRangePreset): boolean {
    if (!this.start || !this.end) return false;
    const now = moment();
    const monthEnded = now.date() === now.clone().endOf('month').date();
    const lastMonth = monthEnded ? now.clone() : now.clone().subtract(1, 'month');

    return this.start.isSame(lastMonth.clone().subtract(preset.months - 1, 'months').startOf('month'), 'day')
      && this.end.isSame(lastMonth.clone().endOf('month'), 'day');
  }

  get isValid(): boolean {
    return !!this.start && !!this.end && !this.end.isBefore(this.start, 'day');
  }

  /** Why Apply is off, so a disabled button is not a dead end. */
  get validationMessage(): string | null {
    if (!this.start || !this.end) return 'Choose a start and an end date.';
    if (this.end.isBefore(this.start, 'day')) return 'The end date is before the start date.';
    return null;
  }

  apply(): void {
    if (!this.isValid) return;
    this.rangeApplied.emit({
      startDate: this.start!.clone(),
      endDate: this.end!.clone(),
    });
  }
}
